import { Policy } from '@commandDrivenStateEngine/core/policies';
import { AccountState, selectors } from '../state';
import { AccountCommand } from '../commands';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import { v4 as uuidv4 } from 'uuid';
import { fetchMembershipRoles } from './fetchMembershipRoles';

const DEFAULT_USERS_REFRESH_POLL_ATTEMPTS = 8;
const DEFAULT_USERS_REFRESH_POLL_DELAY_MS = 300;

export type UserSubmitPolicyOptions = {
  /** Poll delay (ms) between read-after-write refetch attempts. 0 in tests. */
  usersRefreshPollDelayMs?: number;
  /** Max read-after-write refetch attempts before giving up. */
  usersRefreshPollAttempts?: number;
};

/**
 * User Submit Policy
 *
 * Responsibilities:
 * - React to user form submission
 * - Validate form
 * - On add: AddAccountUser
 * - On edit: UpdateUserProfile (identity) + UpdateMembershipRoles (roles)
 * - Refresh users list + per-membership roles on success. On ADD, the refresh
 *   polls the UsersProfiles readmodel until the new user is projected, so the
 *   list updates in place without a manual page reload (read-after-write).
 */
export const createUserSubmitPolicy = (
  backend: ClientLibraryType,
  accountId: string,
  opts: UserSubmitPolicyOptions = {}
): Policy<AccountState, AccountCommand> => ({
  name: 'user-submit',

  shouldReact: ( prev, next ) => {
    return (
      !prev.users.loading.submitting &&
      next.users.loading.submitting &&
      next.users.formMode.type !== 'closed'
    );
  },

  onStateChange: async ({ next, ctx }) => {
    const { formMode, formData } = next.users;

    if ( !selectors.isUserFormValid( next )) {
      const errors: Record<string, string> = {};
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      if ( !formData.email.trim()) {
        errors.email = 'Email es requerido';
      } else if ( !emailRegex.test( formData.email.trim())) {
        errors.email = 'Email no es válido';
      }

      if ( !formData.firstName?.trim()) {
        errors.firstName = 'Nombre es requerido';
      }

      if ( !formData.lastName?.trim()) {
        errors.lastName = 'Apellido es requerido';
      }

      if ( !formData.roles || formData.roles.length === 0 ) {
        errors.roles = 'Al menos un rol es requerido';
      }

      ctx.dispatch(
        { type: 'account/users/form/setErrors', payload: { errors }},
        { source: 'api' }
      );
      ctx.dispatch(
        { type: 'account/users/loading/setSubmitting', payload: { isLoading: false }},
        { source: 'api' }
      );
      return;
    }

    try {
      // The just-added user's id (add path only). Used to wait for its
      // projection into UsersProfiles before refreshing the list.
      let newUserId: string | null = null;

      if ( formMode.type === 'edit' ) {
        const userId = ( formMode as any ).entityId as string;
        const existingUser = selectors.getUserById( next, userId );
        const profileChanged =
          existingUser &&
          ( formData.firstName.trim() !== ( existingUser.firstName ?? '' ) ||
            formData.lastName.trim() !== ( existingUser.lastName ?? '' ) ||
            formData.email.trim() !== existingUser.email );
        if ( profileChanged ) {
          await backend.commands.UpdateUserProfile({
            id: userId,
            body: {
              firstName: formData.firstName.trim(),
              lastName: formData.lastName.trim(),
              email: formData.email.trim()
            }
          });
        }

        const currentRoles = next.users.membershipRoles[userId] ?? [];
        const sortedCurrent = [...currentRoles].sort();
        const sortedNext = [...formData.roles].sort();
        const rolesChanged =
          sortedCurrent.length !== sortedNext.length ||
          sortedCurrent.some(( r, i ) => r !== sortedNext[i]);
        if ( rolesChanged ) {
          await backend.commands.UpdateMembershipRoles({
            id: userId,
            body: {
              accountId,
              roles: formData.roles
            }
          });
        }
      } else {
        newUserId = uuidv4();
        await backend.commands.AddAccountUser({
          id: accountId,
          body: {
            userId: newUserId,
            email: formData.email.trim(),
            firstName: formData.firstName?.trim(),
            lastName: formData.lastName?.trim() || '',
            roles: formData.roles
          }
        });
      }

      // Read-after-write: AddAccountUser writes to the event store, but the
      // UsersProfiles readmodel is projected asynchronously — a one-shot re-fetch
      // reads a STALE list without the new user, so the row only appears after a
      // manual reload. On add, poll until the new user is projected; on edit the
      // row already exists, so a single fetch suffices.
      try {
        const pollAttempts =
          opts.usersRefreshPollAttempts ?? DEFAULT_USERS_REFRESH_POLL_ATTEMPTS;
        const pollDelayMs =
          opts.usersRefreshPollDelayMs ?? DEFAULT_USERS_REFRESH_POLL_DELAY_MS;

        let response = await backend.queries.UsersProfiles();
        for ( let attempt = 1; attempt < pollAttempts && newUserId; attempt++ ) {
          const list = Array.isArray( response ) ? response : [];
          if ( list.some(( u: any ) => u.id === newUserId )) break;
          await new Promise(( resolve ) => setTimeout( resolve, pollDelayMs ));
          response = await backend.queries.UsersProfiles();
        }

        if ( response && Array.isArray( response )) {
          ctx.dispatch(
            { type: 'account/users/data/setAll', payload: { users: response }},
            { source: 'api' }
          );

          const userIds = response.map(( u: any ) => u.id ).filter( Boolean );
          if ( userIds.length > 0 ) {
            const membershipRoles = await fetchMembershipRoles( backend, accountId, userIds );
            ctx.dispatch(
              { type: 'account/users/data/setMembershipRoles', payload: { membershipRoles }},
              { source: 'api' }
            );
          }
        }
      } catch ( fetchError ) {
        console.error( '[account] Failed to refresh users list:', fetchError );
      }

      ctx.dispatch(
        { type: 'account/users/loading/setSubmitting', payload: { isLoading: false }},
        { source: 'api' }
      );
      ctx.dispatch(
        { type: 'account/users/form/close', payload: {}},
        { source: 'api' }
      );
    } catch ( error ) {
      console.error( '[account] User submit error:', error );

      ctx.dispatch(
        { type: 'account/users/loading/setSubmitting', payload: { isLoading: false }},
        { source: 'api' }
      );

      throw error;
    }
  }
});
